/* eslint-disable @typescript-eslint/no-explicit-any */
import { Action } from 'redux'; // Import Action from redux or the appropriate module

import {
  CombinedHandlerActionCreators,
  CombinedHandlerMiddleware,
  CommandHandler,
  CommandHandlerMiddlewareAPI,
} from './abstractions';
import {
  CommandHandlerOutput,
  CommandHandlerDeclaration,
  isPayloadAction,
} from './createCommands';

type HandlersList = CommandHandlerOutput<
  string,
  Record<string, CommandHandlerDeclaration>
>[];

export interface CombineHandlersOptions<THandlerLists extends HandlersList> {
  handlers: THandlerLists;
  onException?: (exception: Error, handlerName: string) => void;
}
/**
 * Merge a set of command handlers into a single Redux middleware
 *
 * TODO(nibeauli): add error handling
 */
export function combineHandlers<
  THandlerLists extends HandlersList>(options: CombineHandlersOptions<THandlerLists>) {
  const initial = {
    handlerMap: {} as Record<string, CommandHandler<any, Action>>,
    // use a generic type for interal use due to loss of keyof/valueof record subtypes, we'll cast strongly on return
    commands: {} as Record<string, Record<string, CommandHandler<any, any>>>,
  };

  const merged = options.handlers.reduce((ret, handler) => {
    // TODO(nibeauli): ensure there are no collisions
    ret.commands[handler.name] = handler.actions;
    Object.assign(ret.handlerMap, handler.caseHandlers);
    return ret;
  }, initial);
  const middleware: CombinedHandlerMiddleware<THandlerLists> =
    (store) => (next) => (action) => {
      if (!isPayloadAction(action)) {
        next(action);
        return;
      }
      // call the command handler. Somehow the type checker believes that callback is always defined.
      const callback = action.type in merged.handlerMap ? merged.handlerMap[action.type] : undefined;
      if (callback) {
        //explicitly cast to fix the type mismatch due to non-closed 'dispatch' type.
        callback(action.payload, store as CommandHandlerMiddlewareAPI<Action>, (error: Error) => {
          options.onException?.(error, action.type);
        });
      }
      next(action);
    };

  return {
    middleware,
    commands: merged.commands as CombinedHandlerActionCreators<THandlerLists>,
  };
}

export { createCommands } from './createCommands';
